import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Loader2, ShoppingBag, User, LogIn, CheckCircle, Copy, Tag, X, MapPin, Check, Package, Gift } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { createOrder, createPendingCheckout, submitOrderUtr, initiatePhonePePayment, useCheckoutSettings, useProducts, useSamplePackSettings, validateOfferCode, isPincodeServiceable, useActiveSiteOffers, useMyActiveOffers } from "@/lib/firestoreService";
import { formatPrice, parseWeightGrams, WHATSAPP_NUMBER } from "@/lib/cart";
import { orderNumber } from "@/lib/orderNumber";
import { useCartContext } from "@/contexts/CartContext";
import { useLang } from "@/lib/i18n";
import { useSEO } from "@/lib/useSEO";
import { asset } from "@/lib/siteAssets";
import { track } from "@/lib/analytics";

const EMPTY_FORM = {
  name: "", phone: "", address: "", city: "", state: "", pincode: "", country: "India", notes: "",
};

const WA_INTL_MESSAGE = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
  "Hi, I'd like to place an order but I'm outside India — can you help me arrange something?"
)}`;

export const CheckoutPage = () => {
  useSEO({ title: "Checkout", description: "Complete your Annapurna Home Pickles order — pay securely via UPI QR code." });
  const { user, profile, firebaseReady, updateMyProfile, saveAddress, signInGoogle } = useAuth();
  const { items, total: subtotal, clear } = useCartContext();
  const navigate = useNavigate();
  const { t } = useLang();
  const settings = useCheckoutSettings();
  const products = useProducts();
  const samplePackSettings = useSamplePackSettings();

  const [step, setStep] = useState("form"); // "form" | "payment"
  const [form, setForm] = useState(EMPTY_FORM);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [phonePeLoading, setPhonePeLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderNum, setOrderNum] = useState(null); // AHP2802... order_number, once created
  // Snapshot of everything needed to create the order + show the payment
  // screen, captured at "Proceed to Payment" time — the order isn't saved
  // until "I've Completed Payment", but the cart/form may already be
  // cleared or changed by then, so the payment screen must not read live
  // cart state (it would show ₹0 once the cart empties).
  const [orderSnapshot, setOrderSnapshot] = useState(null);
  const [showUtrPopup, setShowUtrPopup] = useState(false);
  const [fulfillmentType, setFulfillmentType] = useState("courier");

  useEffect(() => {
    if (!settings.enableCourier && settings.enablePickup) setFulfillmentType("pickup");
    else if (settings.enableCourier) setFulfillmentType("courier");
    else if (settings.enablePickup) setFulfillmentType("pickup");
  }, [settings.enableCourier, settings.enablePickup]);

  // ─── Promo code ──────────────────────────────────────────────────────────
  const [promoInput, setPromoInput] = useState("");
  const [appliedOffer, setAppliedOffer] = useState(null); // { id, code, name, discount_percent }
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState("");

  // Offers this customer can actually browse and one-click apply. Employee
  // referral codes are deliberately excluded — they're never listed
  // anywhere, only redeemable by typing the code an employee handed out.
  const siteOffers = useActiveSiteOffers();
  const myOffers = useMyActiveOffers(user?.id);
  const availableOffers = useMemo(() => {
    const byId = new Map();
    [...siteOffers, ...myOffers].forEach((o) => { if (o.code) byId.set(o.id, o); });
    return [...byId.values()];
  }, [siteOffers, myOffers]);

  // ─── Saved addresses ─────────────────────────────────────────────────────
  // null = "new address" mode (manual entry below, optionally saved back to
  // the address book); any other value is the id of a saved address in use.
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [saveNewAddress, setSaveNewAddress] = useState(false);

  // ─── Derived pricing ─────────────────────────────────────────────────────
  // Client-side preview only — the server (_price_checkout_items) always
  // recomputes weight, the state rate, and the delivery charge itself
  // before charging anything, so this can't be used to under-pay.
  const totalWeightGrams = useMemo(() => items.reduce((sum, it) => {
    if (it.item_type === "combo") {
      const comboWeight = (it.selections || []).reduce((s, sel) => s + parseWeightGrams(sel.size_label), 0);
      return sum + comboWeight * it.quantity;
    }
    if (it.item_type === "sample_pack") {
      const pack = samplePackSettings.packs?.[it.pack_type];
      const productCount = Object.keys(pack?.productPrices || {}).length;
      const packWeight = productCount * parseWeightGrams(pack?.fixedSizeLabel || "150g");
      return sum + packWeight * it.quantity;
    }
    return sum + parseWeightGrams(it.size) * it.quantity;
  }, 0), [items, samplePackSettings]);

  const stateRatePerKg = settings.stateShippingRates?.[form.state] ?? settings.stateShippingRates?.["Other States"] ?? 120;

  // The state rate is a flat base charge covering any order up to 1kg (even
  // a tiny order pays it in full — no minimum order weight). Weight above
  // 1kg adds one quarter of the base rate, rounded up to the nearest rupee,
  // per extra 250g bracket, rounded up. Mirrors public._price_checkout_items.
  const courierDeliveryCharge = (() => {
    if (totalWeightGrams <= 1000) return stateRatePerKg;
    const extraGrams = totalWeightGrams - 1000;
    const extraBrackets = Math.ceil(extraGrams / 250);
    const quarterRate = Math.ceil(stateRatePerKg / 4);
    return stateRatePerKg + extraBrackets * quarterRate;
  })();

  const discountAmount  = appliedOffer ? Math.round(subtotal * appliedOffer.discount_percent / 100) : 0;
  const taxableAfterDiscount = subtotal - discountAmount;
  const gstAmount       = Math.round(taxableAfterDiscount * settings.gstPercent / 100);
  const deliveryCharge  = fulfillmentType === "courier" ? courierDeliveryCharge : 0;
  const grandTotal      = taxableAfterDiscount + gstAmount + deliveryCharge;

  useEffect(() => {
    if (items.length === 0 && step === "form") navigate("/products");
  }, [items, navigate, step]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const selectSavedAddress = (a) => {
    setSelectedAddressId(a.id);
    setSaveNewAddress(false);
    setForm(f => ({
      ...f,
      name:    a.name    || f.name,
      phone:   a.phone   || f.phone,
      address: a.address || "",
      city:    a.city    || "",
      state:   a.state   || "",
      pincode: a.pincode || "",
      country: a.country || "India",
    }));
  };

  const useNewAddress = () => {
    setSelectedAddressId(null);
    setForm(f => ({ ...f, address: "", city: "", state: "", pincode: "", country: "India" }));
  };

  // Auto-fill once, the first time the profile becomes available — prefers
  // a saved default address, then the most recent order's address, then
  // just name/phone. Runs once (not on every profile change) so it never
  // clobbers something the customer has already picked or typed.
  const autoFilledRef = useRef(false);
  useEffect(() => {
    if (!user || !profile || autoFilledRef.current) return;
    autoFilledRef.current = true;
    const addresses = profile.addresses || [];
    const defaultAddr = addresses.find(a => a.isDefault) || addresses[0];
    if (defaultAddr) {
      selectSavedAddress(defaultAddr);
    } else {
      setForm(f => ({
        ...f,
        name:    f.name    || profile.name                 || "",
        phone:   f.phone   || profile.phone                || "",
        address: f.address || profile.lastAddress?.address || "",
        city:    f.city    || profile.lastAddress?.city    || "",
        state:   f.state   || profile.lastAddress?.state   || "",
        pincode: f.pincode || profile.lastAddress?.pincode || "",
        country: f.country || profile.lastAddress?.country || "India",
      }));
    }
  }, [user, profile]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the user presses Back from the PhonePe site, browsers restore the
  // page from bfcache with stale state (phonePeLoading=true). Reset it.
  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) {
        setPhonePeLoading(false);
        setOrderId(null);
        setOrderNum(null);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const isCourier = fulfillmentType === "courier";
  const isNonIndia = isCourier && form.country !== "India";

  // Selecting "Other" as the country is a dead end — nudge them straight to
  // WhatsApp instead of leaving them stuck on a form they can't submit.
  // Delayed (not instant) so the "Redirecting…" message is actually visible
  // rather than the page just vanishing under them.
  useEffect(() => {
    if (!isNonIndia) return;
    track("checkout_blocked_non_india", { country: form.country, is_guest: !user });
    const id = setTimeout(() => { window.location.href = WA_INTL_MESSAGE; }, 3000);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNonIndia]);

  // Catches combo/sample-pack lines too — previously this only checked
  // it.product_id, which regular cart lines have but combo/sample-pack
  // lines don't, so a sold-out product inside one of those silently slipped
  // through to the payment screen and only failed when "I've Completed
  // Payment" hit the server (a worse moment to find out).
  const isProductSoldOut = (productId) => products?.find(p => p.id === productId)?.in_stock === false;

  const validate = () => {
    const soldOutItem = items.find(it => {
      if (it.item_type === "combo") {
        return (it.selections || []).some(s => isProductSoldOut(s.product_id));
      }
      if (it.item_type === "sample_pack") {
        const packProductIds = Object.keys(samplePackSettings.packs?.[it.pack_type]?.productPrices || {});
        return packProductIds.some(isProductSoldOut);
      }
      return isProductSoldOut(it.product_id);
    });
    if (soldOutItem) { toast.error(`${soldOutItem.name} is now sold out — please remove it from your cart.`); return false; }
    if (!form.name.trim())     { toast.error(t.checkout.enter_name);      return false; }
    if (!form.phone.trim())    { toast.error(t.checkout.enter_phone);     return false; }
    if (isCourier) {
      if (!form.address.trim())  { toast.error(t.checkout.enter_address);   return false; }
      if (!form.city.trim())     { toast.error(t.checkout.enter_city);      return false; }
      if (!form.state.trim())    { toast.error(t.checkout.enter_state);     return false; }
      if (!form.pincode.trim())  { toast.error(t.checkout.enter_pincode);   return false; }
      // The country dropdown only offers "India"/"Other" and "Other" is
      // already blocked from reaching this point via the disabled Proceed
      // button — this is just a defense-in-depth backstop, real enforcement
      // is server-side in _price_checkout_items regardless.
      if (isNonIndia) {
        toast.error("For international orders, please contact us on WhatsApp.");
        return false;
      }
      if (!isPincodeServiceable(form.pincode, settings)) {
        toast.error("Sorry, we don't currently deliver to this pincode. Message us on WhatsApp to check.");
        return false;
      }
    }
    return true;
  };

  // codeOverride lets the "available offers" dropdown apply immediately on
  // selection without waiting on the promoInput state update to land first
  // — its presence also doubles as how we tell dropdown picks apart from
  // manually-typed codes for analytics, without needing separate state.
  const applyPromoCode = async (codeOverride) => {
    const code = (codeOverride ?? promoInput).trim();
    if (!code) return;
    const source = codeOverride ? "dropdown" : "manual_input";
    setPromoChecking(true);
    setPromoError("");
    try {
      const offer = await validateOfferCode(code, user?.id, {
        employeeReferralUsed: !!profile?.employee_referral_used_at,
      });
      if (!offer) {
        const reason = "That code isn't valid or has expired.";
        setPromoError(reason);
        track("offer_code_apply_failed", { offer_code: code, source, reason, is_guest: !user });
        return;
      }
      setAppliedOffer(offer);
      setPromoInput(offer.code || code);
      track("offer_code_applied", {
        offer_code: offer.code,
        discount_percent: offer.discount_percent,
        offer_scope: offer.scope,
        source,
        is_guest: !user,
      });
      toast.success(`${offer.discount_percent}% discount applied!`);
    } catch (e) {
      const reason = e.message || "Could not check that code.";
      setPromoError(reason);
      track("offer_code_apply_failed", { offer_code: code, source, reason, is_guest: !user });
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromoCode = () => {
    setAppliedOffer(null);
    setPromoInput("");
    setPromoError("");
  };

  // Step 1 → 2: just validate and snapshot — the order itself isn't saved
  // yet. It's only created once they confirm they've actually paid.
  const handlePlaceOrder = () => {
    if (!validate()) return;
    track("checkout_started", {
      cart_value: grandTotal,
      subtotal,
      gst_percent: settings.gstPercent,
      gst_amount: gstAmount,
      delivery_charge: deliveryCharge,
      order_weight_grams: isCourier ? totalWeightGrams : null,
      delivery_state: isCourier ? form.state : null,
      state_rate_per_kg: isCourier ? stateRatePerKg : null,
      discount_amount: discountAmount,
      item_count: items.reduce((s, it) => s + it.quantity, 0),
      unique_product_count: items.length,
      product_names: items.map(it => it.name).join(", "),
      fulfillment_type: fulfillmentType,
      has_promo_code: !!appliedOffer,
      offer_code: appliedOffer?.code || null,
      discount_percent: appliedOffer?.discount_percent || 0,
      is_guest: !user,
      using_saved_address: isCourier ? selectedAddressId !== null : null,
    });
    setOrderSnapshot({
      form: { ...form },
      items: items.map(it => {
        if (it.item_type === "combo") {
          return {
            item_type: "combo",
            combo_type: it.combo_type,
            name: it.name,
            size: it.size,
            price: it.price,
            quantity: it.quantity,
            selections: it.selections,
          };
        }
        if (it.item_type === "sample_pack") {
          return {
            item_type: "sample_pack",
            pack_type: it.pack_type,
            name: it.name,
            size: it.size,
            price: it.price,
            quantity: it.quantity,
          };
        }
        return {
          product_id: it.product_id,
          name: it.name,
          slug: it.slug,
          image_url: it.image_url,
          size: it.size,
          price: it.price,
          quantity: it.quantity,
        };
      }),
      subtotal,
      offerCode: appliedOffer?.code || null,
      discountPercent: appliedOffer?.discount_percent || 0,
      discountAmount,
      gstPercent: settings.gstPercent,
      gstAmount,
      deliveryCharge,
      orderWeightGrams: totalWeightGrams,
      grandTotal,
      fulfillmentType,
      saveNewAddress: saveNewAddress && selectedAddressId === null,
    });
    setStep("payment");
  };

  // "I've Completed Payment" — this is what actually creates the order now.
  const handlePaid = async () => {
    if (orderId) { setShowUtrPopup(true); return; } // guard against double-click
    if (!orderSnapshot) return;
    if (!firebaseReady) {
      toast.error("Orders are unavailable — please check your connection and try again.");
      return;
    }
    setPlacingOrder(true);
    try {
      const s = orderSnapshot;
      const { id, order_number } = await createOrder({
        uid: user?.id || null,
        email: user?.email || null,
        customer_name: s.form.name.trim(),
        phone: s.form.phone.trim(),
        address: s.form.address.trim(),
        city: s.form.city.trim(),
        state: s.form.state.trim(),
        pincode: s.form.pincode.trim(),
        country: s.form.country.trim(),
        notes: s.form.notes.trim(),
        offer_code: s.offerCode,
        fulfillment_type: s.fulfillmentType,
        items: s.items,
      });

      track("order_placed", {
        order_id: id,
        order_number,
        order_total: s.grandTotal,
        subtotal: s.subtotal,
        gst_percent: s.gstPercent,
        gst_amount: s.gstAmount,
        delivery_charge: s.deliveryCharge,
        order_weight_grams: s.fulfillmentType === "courier" ? s.orderWeightGrams : null,
        item_count: s.items.reduce((sum, it) => sum + it.quantity, 0),
        unique_product_count: s.items.length,
        product_names: s.items.map(it => it.name).join(", "),
        fulfillment_type: s.fulfillmentType,
        offer_code: s.offerCode,
        discount_percent: s.discountPercent,
        discount_amount: s.discountAmount,
        delivery_city: s.fulfillmentType === "courier" ? s.form.city.trim() : null,
        delivery_state: s.fulfillmentType === "courier" ? s.form.state.trim() : null,
        delivery_country: s.fulfillmentType === "courier" ? s.form.country.trim() : null,
        is_guest: !user,
        // Signed-in customers are identified via Mixpanel's People profile
        // (identify() + setProfile in AuthContext) — name/email live there,
        // not on every event. Guests never identify(), so this is the only
        // place their identity is recoverable without a Supabase lookup.
        guest_customer_name: !user ? s.form.name.trim() : null,
        guest_customer_phone: !user ? s.form.phone.trim() : null,
      });

      if (user) {
        // Best-effort: the order above already succeeded, so a failure here
        // (e.g. remembering the address for next time) must never surface
        // as an "order failed" error to the customer.
        try {
          await updateMyProfile({
            name: s.form.name.trim(),
            phone: s.form.phone.trim(),
            lastAddress: {
              address: s.form.address.trim(),
              city: s.form.city.trim(),
              state: s.form.state.trim(),
              pincode: s.form.pincode.trim(),
              country: s.form.country.trim(),
            },
          });
        } catch (e) {
          console.error("Could not save profile after order:", e);
        }

        if (s.saveNewAddress) {
          try {
            await saveAddress({
              name: s.form.name.trim(),
              phone: s.form.phone.trim(),
              address: s.form.address.trim(),
              city: s.form.city.trim(),
              state: s.form.state.trim(),
              pincode: s.form.pincode.trim(),
              country: s.form.country.trim(),
            });
          } catch (e) {
            console.error("Could not save address to address book:", e);
          }
        }
      }

      setOrderId(id);
      setOrderNum(order_number);
      clear(); // order is saved now — clear the cart so the navbar updates
      setShowUtrPopup(true);
    } catch (err) {
      toast.error(t.checkout.order_failed + " " + (err?.message || ""));
      // `s` (the local alias for orderSnapshot) is block-scoped to the try{}
      // above and isn't visible here — read orderSnapshot directly instead.
      track("order_placement_failed", {
        payment_mode: "upi_manual",
        error_message: err?.message || null,
        order_total: orderSnapshot?.grandTotal ?? null,
        fulfillment_type: orderSnapshot?.fulfillmentType ?? null,
        offer_code: orderSnapshot?.offerCode ?? null,
        is_guest: !user,
      });
    } finally {
      setPlacingOrder(false);
    }
  };

  const closeUtrPopup = () => {
    setShowUtrPopup(false);
    toast.success(t.checkout.order_success);
    navigate(orderId ? `/order/${orderId}` : "/");
  };

  // PhonePe payment: stages the checkout (not a real order yet — see
  // createPendingCheckout) then hands off to the PhonePe gateway. The real
  // order is only created once the gateway confirms payment; orderId here
  // temporarily holds the pending-checkout id until then.
  const handlePhonePePayment = async (snap = null) => {
    if (orderId || phonePeLoading) return; // guard against double-click creating two pending checkouts
    const s = snap ?? orderSnapshot;
    if (!s) return;
    if (!firebaseReady) {
      toast.error("Orders are unavailable — please check your connection and try again.");
      return;
    }
    setPhonePeLoading(true);
    try {
      const { id } = await createPendingCheckout({
        uid: user?.id || null,
        email: user?.email || null,
        customer_name: s.form.name.trim(),
        phone: s.form.phone.trim(),
        address: s.form.address.trim(),
        city: s.form.city.trim(),
        state: s.form.state.trim(),
        pincode: s.form.pincode.trim(),
        country: s.form.country.trim(),
        notes: s.form.notes.trim(),
        offer_code: s.offerCode,
        fulfillment_type: s.fulfillmentType,
        items: s.items,
      });

      setOrderId(id);

      track("phonepe_payment_initiated", {
        checkout_id: id,
        order_total: s.grandTotal,
        order_weight_grams: s.fulfillmentType === "courier" ? s.orderWeightGrams : null,
        delivery_state: s.fulfillmentType === "courier" ? s.form.state.trim() : null,
        is_guest: !user,
      });

      // Best-effort profile + address update — same as the UPI flow
      if (user) {
        try {
          await updateMyProfile({
            name: s.form.name.trim(),
            phone: s.form.phone.trim(),
            lastAddress: {
              address: s.form.address.trim(),
              city: s.form.city.trim(),
              state: s.form.state.trim(),
              pincode: s.form.pincode.trim(),
              country: s.form.country.trim(),
            },
          });
        } catch (e) {
          console.error("Could not save profile before PhonePe redirect:", e);
        }
        if (s.saveNewAddress) {
          try {
            await saveAddress({
              name: s.form.name.trim(),
              phone: s.form.phone.trim(),
              address: s.form.address.trim(),
              city: s.form.city.trim(),
              state: s.form.state.trim(),
              pincode: s.form.pincode.trim(),
              country: s.form.country.trim(),
            });
          } catch (e) {
            console.error("Could not save address before PhonePe redirect:", e);
          }
        }
      }

      const redirectUrl = await initiatePhonePePayment(id);
      // Don't clear the cart here — payment hasn't succeeded yet. If it fails
      // and the customer retries, /checkout needs the cart intact. The cart
      // is cleared on PaymentStatusPage once the webhook confirms payment.
      window.location.href = redirectUrl;
    } catch (err) {
      toast.error("Could not start PhonePe payment: " + (err?.message || "Please try UPI instead."));
      track("phonepe_payment_initiation_failed", {
        error_message: err?.message || null,
        order_total: s?.grandTotal ?? null,
        fulfillment_type: s?.fulfillmentType ?? null,
        offer_code: s?.offerCode ?? null,
        is_guest: !user,
      });
      // The order (if created) was cancelled server-side on failure — clear
      // orderId so the double-click guard doesn't block a fresh retry.
      setOrderId(null);
      setOrderNum(null);
      setPhonePeLoading(false);
    }
  };

  const handleDirectPhonePe = () => {
    if (!validate()) return;
    const snapshot = {
      form: { ...form },
      items: items.map(it => {
        if (it.item_type === "combo") {
          return {
            item_type: "combo",
            combo_type: it.combo_type,
            name: it.name,
            size: it.size,
            price: it.price,
            quantity: it.quantity,
            selections: it.selections,
          };
        }
        if (it.item_type === "sample_pack") {
          return {
            item_type: "sample_pack",
            pack_type: it.pack_type,
            name: it.name,
            size: it.size,
            price: it.price,
            quantity: it.quantity,
          };
        }
        return {
          product_id: it.product_id,
          name: it.name,
          slug: it.slug,
          image_url: it.image_url,
          size: it.size,
          price: it.price,
          quantity: it.quantity,
        };
      }),
      subtotal,
      offerCode: appliedOffer?.code || null,
      discountPercent: appliedOffer?.discount_percent || 0,
      discountAmount,
      gstPercent: settings.gstPercent,
      gstAmount,
      deliveryCharge,
      orderWeightGrams: totalWeightGrams,
      grandTotal,
      fulfillmentType,
      saveNewAddress: saveNewAddress && selectedAddressId === null,
    };
    track("checkout_started", {
      cart_value: grandTotal,
      subtotal,
      gst_percent: settings.gstPercent,
      gst_amount: gstAmount,
      delivery_charge: deliveryCharge,
      order_weight_grams: isCourier ? totalWeightGrams : null,
      delivery_state: isCourier ? form.state : null,
      state_rate_per_kg: isCourier ? stateRatePerKg : null,
      discount_amount: discountAmount,
      item_count: items.reduce((s, it) => s + it.quantity, 0),
      unique_product_count: items.length,
      product_names: items.map(it => it.name).join(", "),
      fulfillment_type: fulfillmentType,
      has_promo_code: !!appliedOffer,
      offer_code: appliedOffer?.code || null,
      discount_percent: appliedOffer?.discount_percent || 0,
      is_guest: !user,
      using_saved_address: isCourier ? selectedAddressId !== null : null,
    });
    handlePhonePePayment(snapshot);
  };

  const copyOrderId = () => {
    if (!orderId) return;
    navigator.clipboard.writeText(orderNumber({ id: orderId, order_number: orderNum }));
    toast.success(t.checkout.copied);
  };

  // ─── Guest checkout blocked ───────────────────────────────────────────────
  if (!settings.enableGuestCheckout && !user && step === "form") {
    return (
      <div className="min-h-screen bg-[#F5E6CC] grid place-items-center px-4">
        <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-10 max-w-sm w-full text-center">
          <div className="w-14 h-14 rounded-full bg-[#5A2D1D]/10 grid place-items-center mx-auto mb-4">
            <LogIn className="w-6 h-6 text-[#5A2D1D]" />
          </div>
          <h2 className="font-display text-2xl font-bold text-[#5A2D1D] mb-2">{t.checkout.signin_to_checkout}</h2>
          <p className="text-sm text-[#2E1A12]/70 mb-6">{t.checkout.signin_description}</p>
          <button
            onClick={signInGoogle}
            className="w-full bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] py-3 rounded-full font-bold transition-colors mb-3"
          >
            {t.checkout.continue_google}
          </button>
          <Link to="/products" className="text-sm text-[#556B2F] hover:underline">
            {t.checkout.back_to_products}
          </Link>
        </div>
      </div>
    );
  }

  // ─── STEP 2: Payment QR ───────────────────────────────────────────────────
  if (step === "payment") {
    const snap = orderSnapshot;
    return (
      <div className="min-h-screen bg-[#F5E6CC] py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg mx-auto">
          <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-8 text-center">

            <div className="w-16 h-16 rounded-full bg-[#556B2F] text-[#F5E6CC] grid place-items-center mx-auto mb-4">
              <ShoppingBag className="w-7 h-7" />
            </div>

            <h1 className="font-display text-2xl font-bold text-[#5A2D1D]">{t.checkout.complete_payment}</h1>
            <p className="text-sm text-[#2E1A12]/70 mt-2">{t.checkout.scan_qr}</p>

            {orderId && (
              <div className="mt-4 inline-flex items-center gap-2 bg-[#EFD9B4] border border-[#5A2D1D]/20 rounded-sm px-4 py-2">
                <span className="text-xs text-[#556B2F] font-bold uppercase tracking-widest">{t.checkout.order_id}</span>
                <span className="font-display font-bold text-[#5A2D1D]">{orderNumber({ id: orderId, order_number: orderNum })}</span>
                <button onClick={copyOrderId} className="text-[#5A2D1D]/50 hover:text-[#5A2D1D]">
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* QR code — shown only when UPI QR checkout is enabled */}
            {settings.enableQrCheckout && (
              <div className="mt-6 bg-white rounded-sm p-4 inline-block border-2 border-[#5A2D1D]/20 shadow-md">
                <img
                  src={asset("payment/qrCode.png")}
                  alt="Payment QR Code"
                  loading="lazy"
                  decoding="async"
                  className="w-56 h-56 object-contain mx-auto"
                />
              </div>
            )}

            {/* Payment breakdown */}
            <div className="mt-5 bg-[#EFD9B4]/60 border border-[#5A2D1D]/15 rounded-sm p-4 text-left space-y-1.5 text-sm">
              <PriceLine label={t.checkout.items_subtotal} amount={snap?.subtotal ?? 0} />
              {snap?.offerCode && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#556B2F]">Discount ({snap.discountPercent}% · {snap.offerCode})</span>
                  <span className="font-semibold text-[#556B2F]">-{formatPrice(snap.discountAmount)}</span>
                </div>
              )}
              {snap?.discountAmount > 0 && (
                <div className="text-xs text-[#556B2F] font-semibold">🎉 You saved {formatPrice(snap.discountAmount)} on this order!</div>
              )}
              <PriceLine label={`GST (${snap?.gstPercent ?? settings.gstPercent}%)`} amount={snap?.gstAmount ?? 0} />
              <PriceLine label={t.checkout.delivery} amount={snap?.deliveryCharge ?? 0} free freeLabel={t.checkout.free} />
              {snap?.fulfillmentType === "courier" && snap?.orderWeightGrams != null && (
                <>
                  <div className="flex justify-between text-xs text-[#2E1A12]/60">
                    <span>Order weight</span>
                    <span>{(snap.orderWeightGrams / 1000).toFixed(2)} kg</span>
                  </div>
                  <p className="text-[11px] text-[#2E1A12]/50">
                    Delivery charge covers orders up to 1kg. Heavier orders are charged extra per 250g.
                  </p>
                </>
              )}
              <div className="border-t border-[#5A2D1D]/20 pt-2 flex justify-between font-display font-bold text-[#5A2D1D]">
                <span>{t.checkout.total_to_pay}</span>
                <span className="text-xl">{formatPrice(snap?.grandTotal ?? 0)}</span>
              </div>
            </div>

            {settings.enableQrCheckout ? (
              <>
                <div className="mt-4 text-xs text-[#2E1A12]/60 space-y-1">
                  <p>{t.checkout.scan_with}</p>
                  <p>{t.checkout.add_order_id}</p>
                </div>

                <button
                  onClick={handlePaid}
                  disabled={placingOrder || phonePeLoading}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/60 text-[#F5E6CC] py-4 rounded-full font-bold text-lg transition-colors"
                >
                  {placingOrder ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                  {t.checkout.paid_button}
                </button>
              </>
            ) : (
              <>
                <p className="mt-4 text-sm text-[#2E1A12]/70">Click below to pay securely via PhonePe — cards, UPI, and net banking accepted.</p>

                <button
                  onClick={handlePhonePePayment}
                  disabled={phonePeLoading}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#5520A8] hover:bg-[#6B30C7] disabled:bg-[#5520A8]/60 text-white py-4 rounded-full font-bold text-lg transition-colors"
                >
                  {phonePeLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {phonePeLoading ? "Redirecting to PhonePe…" : "Pay with PhonePe"}
                </button>
              </>
            )}

            <p className="mt-3 text-xs font-semibold text-[#556B2F] bg-[#556B2F]/10 border border-[#556B2F]/30 rounded-sm px-3 py-2">
              {t.checkout.team_confirm}
            </p>
          </div>
        </div>

        {showUtrPopup && (
          <UtrPopup
            onSubmit={async (utr) => {
              try {
                await submitOrderUtr(orderId, utr);
                track("utr_submitted", { order_id: orderId, utr_value: utr, is_edit: false });
                closeUtrPopup();
              } catch (e) {
                toast.error(e.message || "Could not save the UTR. You can add it later from your order page.");
              }
            }}
            onSkip={closeUtrPopup}
          />
        )}
      </div>
    );
  }

  // ─── STEP 1: Delivery Form ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F5E6CC] py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">

        <Link to="/products" className="inline-flex items-center gap-2 text-sm text-[#5A2D1D] font-semibold hover:underline mb-6">
          <ArrowLeft className="w-4 h-4" /> {t.checkout.back_to_products}
        </Link>

        {/* Steps indicator */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#5A2D1D] text-[#F5E6CC] grid place-items-center text-sm font-bold">1</div>
            <span className="text-sm font-bold text-[#5A2D1D]">{t.checkout.delivery_details}</span>
          </div>
          <div className="flex-1 h-px bg-[#5A2D1D]/20" />
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full border-2 border-[#5A2D1D]/30 text-[#5A2D1D]/40 grid place-items-center text-sm font-bold">2</div>
            <span className="text-sm font-bold text-[#5A2D1D]/40">{t.checkout.payment}</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">

          {/* LEFT — Order Summary */}
          <div className="bg-[#EFD9B4]/60 border border-[#5A2D1D]/20 rounded-sm p-6">
            <h2 className="font-display text-xl font-bold text-[#5A2D1D] mb-5 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" /> {t.checkout.order_summary}
            </h2>

            <div className="space-y-4">
              {items.map(it => (
                <div key={it.key} className="flex gap-4 items-center border-b border-[#5A2D1D]/10 pb-4">
                  {it.image_url ? (
                    <img src={it.image_url} alt={it.name} loading="lazy" decoding="async" className="w-16 h-16 object-cover rounded-sm flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-sm flex-shrink-0 bg-[#556B2F]/10 text-[#556B2F] grid place-items-center">
                      {it.item_type === "sample_pack" ? <Gift className="w-6 h-6" /> : <Package className="w-6 h-6" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-[#5A2D1D] truncate">{it.name}</div>
                    <div className="text-xs text-[#556B2F] font-bold uppercase tracking-widest">{it.size}</div>
                    <div className="text-sm text-[#2E1A12]/70 mt-0.5">{t.checkout.qty}: {it.quantity}</div>
                  </div>
                  <div className="font-bold text-[#5A2D1D] whitespace-nowrap">
                    {formatPrice(it.price * it.quantity)}
                  </div>
                </div>
              ))}
            </div>

            {/* Promo code */}
            <div className="mt-4">
              {appliedOffer ? (
                <div className="flex items-center justify-between gap-2 bg-[#556B2F]/10 border border-[#556B2F]/30 rounded-sm px-3 py-2.5">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[#556B2F]">
                    <Tag className="w-4 h-4" />
                    <span className="font-mono">{appliedOffer.code}</span> applied — {appliedOffer.discount_percent}% off
                  </div>
                  <button onClick={removePromoCode} className="text-[#556B2F]/70 hover:text-[#556B2F]">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {availableOffers.length > 0 && (
                    <select
                      value=""
                      onChange={(e) => { if (e.target.value) applyPromoCode(e.target.value); }}
                      disabled={promoChecking}
                      className="w-full px-3 py-2.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
                    >
                      <option value="">Select an available offer…</option>
                      {availableOffers.map((o) => (
                        <option key={o.id} value={o.code}>
                          {o.code} — {o.discount_percent}% off ({o.name})
                        </option>
                      ))}
                    </select>
                  )}
                  <div className="flex gap-2">
                    <input
                      value={promoInput}
                      onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoError(""); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromoCode(); } }}
                      placeholder="Have a promo code?"
                      className="flex-1 px-3 py-2.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm font-mono uppercase text-[#2E1A12] placeholder:font-sans placeholder:normal-case placeholder:text-[#2E1A12]/40"
                    />
                    <button
                      onClick={() => applyPromoCode()}
                      disabled={promoChecking || !promoInput.trim()}
                      className="px-4 py-2.5 rounded-sm bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] text-sm font-bold transition-colors"
                    >
                      {promoChecking ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                    </button>
                  </div>
                  {promoError && (
                    <div className="text-xs text-red-600 font-semibold" data-testid="promo-error">{promoError}</div>
                  )}
                </div>
              )}
            </div>

            {/* Price breakdown */}
            <div className="mt-5 pt-4 border-t border-[#5A2D1D]/20 space-y-2">
              <PriceLine label={t.checkout.items_subtotal} amount={subtotal} />
              {appliedOffer && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#556B2F]">Discount ({appliedOffer.discount_percent}%)</span>
                  <span className="font-semibold text-[#556B2F]">-{formatPrice(discountAmount)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="text-xs text-[#556B2F] font-semibold">🎉 You saved {formatPrice(discountAmount)} on this order!</div>
              )}
              <PriceLine label={`GST (${settings.gstPercent}%)`} amount={gstAmount} />
              {isCourier && (
                <>
                  <PriceLine label={t.checkout.courier_delivery} amount={deliveryCharge} free freeLabel={t.checkout.free} />
                  <div className="flex justify-between text-xs text-[#2E1A12]/60">
                    <span>Order weight</span>
                    <span>{(totalWeightGrams / 1000).toFixed(2)} kg</span>
                  </div>
                  {totalWeightGrams < 1000 && (
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] text-[#2E1A12]/50 truncate" title="Delivery charges currently applied is up to 1KG">
                        Delivery charges currently applied is up to 1KG
                      </p>
                      <Link
                        to="/products"
                        onClick={() => track("checkout_add_more_products_clicked", { order_weight_grams: totalWeightGrams, source: "delivery_note" })}
                        className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#556B2F] hover:underline whitespace-nowrap"
                      >
                        <ShoppingBag className="w-3 h-3" /> Add more
                      </Link>
                    </div>
                  )}
                </>
              )}
              <div className="border-t-2 border-[#5A2D1D]/20 pt-3 flex items-center justify-between">
                <span className="font-bold text-[#2E1A12] uppercase tracking-widest text-sm">{t.checkout.grand_total}</span>
                <span className="font-display text-2xl font-bold text-[#5A2D1D]">{formatPrice(grandTotal)}</span>
              </div>
            </div>

            {settings.enableQrCheckout && (
              <p className="text-xs text-[#2E1A12]/60 mt-4 leading-relaxed">{t.checkout.upi_note}</p>
            )}
          </div>

          {/* RIGHT — Delivery Form */}
          <div className="bg-[#F5E6CC] border border-[#5A2D1D]/20 rounded-sm p-6">

            {firebaseReady && !user && (
              <div className="mb-5 p-4 bg-[#EFD9B4] border border-[#5A2D1D]/20 rounded-sm flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-[#5A2D1D]">{t.checkout.have_account}</div>
                  <div className="text-xs text-[#2E1A12]/70">{t.checkout.signin_to_track}</div>
                </div>
                <button
                  onClick={signInGoogle}
                  className="inline-flex items-center gap-2 text-xs font-bold text-[#5A2D1D] border border-[#5A2D1D]/40 px-3 py-2 rounded-sm hover:bg-[#5A2D1D]/5 whitespace-nowrap"
                >
                  <LogIn className="w-3.5 h-3.5" /> {t.checkout.sign_in}
                </button>
              </div>
            )}

            {user && (
              <div className="mb-5 flex items-center gap-2 text-sm text-[#556B2F] font-semibold">
                <User className="w-4 h-4" /> {t.checkout.signed_in_as} {profile?.name || user.email}
              </div>
            )}

            <h2 className="font-display text-xl font-bold text-[#5A2D1D] mb-4">{t.checkout.how_receive}</h2>

            {(settings.enablePickup || settings.enableCourier) && (
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                {settings.enablePickup && (
                  <button
                    type="button"
                    onClick={() => setFulfillmentType("pickup")}
                    className={`flex items-center justify-center gap-2 p-2.5 rounded-sm border-2 transition-colors ${fulfillmentType === "pickup" ? "border-[#5A2D1D] bg-[#5A2D1D]/5" : "border-[#5A2D1D]/20 hover:border-[#5A2D1D]/40"}`}
                  >
                    <span className="text-lg">📍</span>
                    <span className="font-bold text-[#5A2D1D] text-sm">Pickup Order</span>
                  </button>
                )}
                {settings.enableCourier && (
                  <button
                    type="button"
                    onClick={() => setFulfillmentType("courier")}
                    className={`flex items-center justify-center gap-2 p-2.5 rounded-sm border-2 transition-colors ${fulfillmentType === "courier" ? "border-[#5A2D1D] bg-[#5A2D1D]/5" : "border-[#5A2D1D]/20 hover:border-[#5A2D1D]/40"}`}
                  >
                    <span className="text-lg">🚚</span>
                    <span className="font-bold text-[#5A2D1D] text-sm">Delivery Order</span>
                  </button>
                )}
              </div>
            )}

            {fulfillmentType === "pickup" && settings.pickupAddress && (
              <div className="mb-5 p-3 bg-[#556B2F]/10 border border-[#556B2F]/25 rounded-sm text-sm">
                <div className="font-bold text-[#556B2F] mb-1">{t.checkout.pickup_at}</div>
                <div className="text-[#2E1A12]/80">{settings.pickupAddress.name}</div>
                <div className="text-[#2E1A12]/70">{settings.pickupAddress.address}</div>
                <div className="text-[#2E1A12]/70">{[settings.pickupAddress.city, settings.pickupAddress.state, settings.pickupAddress.pincode].filter(Boolean).join(", ")}</div>
                {settings.pickupAddress.note && <div className="text-xs text-[#2E1A12]/60 mt-2">{settings.pickupAddress.note}</div>}
              </div>
            )}

            {fulfillmentType === "courier" && (
              <p className="mb-4 text-xs text-[#2E1A12]/60 bg-[#EFD9B4]/50 border border-[#5A2D1D]/15 rounded-sm px-3 py-2">
                {settings.courierNote || t.checkout.courier_note}
              </p>
            )}

            <h2 className="font-display text-lg font-bold text-[#5A2D1D] mb-4">{isCourier ? t.checkout.delivery_details : t.checkout.contact_details}</h2>

            {/* Saved addresses — courier only */}
            {isCourier && user && profile?.addresses?.length > 0 && (
              <div className="mb-5">
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-2">
                  Saved Addresses
                </div>
                <div className="space-y-2">
                  {profile.addresses.map(a => {
                    const active = selectedAddressId === a.id;
                    return (
                      <button
                        key={a.id}
                        type="button"
                        onClick={() => selectSavedAddress(a)}
                        data-mp-block=""
                        className={`w-full text-left p-3 rounded-sm border-2 transition-colors flex items-start gap-2.5 ${
                          active ? "border-[#5A2D1D] bg-[#5A2D1D]/5" : "border-[#5A2D1D]/20 hover:border-[#5A2D1D]/40"
                        }`}
                      >
                        <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? "text-[#5A2D1D]" : "text-[#5A2D1D]/40"}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-[#5A2D1D] text-sm">{a.name}</span>
                            {a.isDefault && (
                              <span className="text-[9px] font-bold text-[#556B2F] uppercase tracking-widest">Default</span>
                            )}
                          </div>
                          <div className="text-xs text-[#2E1A12]/70 mt-0.5 truncate">
                            {a.address}, {[a.city, a.state, a.pincode].filter(Boolean).join(", ")}
                          </div>
                        </div>
                        {active && <Check className="w-4 h-4 text-[#5A2D1D] flex-shrink-0 mt-0.5" />}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={useNewAddress}
                    className={`w-full text-left p-3 rounded-sm border-2 border-dashed text-sm font-semibold transition-colors ${
                      selectedAddressId === null ? "border-[#5A2D1D] bg-[#5A2D1D]/5 text-[#5A2D1D]" : "border-[#5A2D1D]/30 text-[#5A2D1D]/70 hover:border-[#5A2D1D]/50"
                    }`}
                  >
                    + Use a new address
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label={t.checkout.full_name} value={form.name} onChange={v => set("name", v)} placeholder={t.checkout.full_name} />
                <Field label={t.checkout.phone_number} value={form.phone} onChange={v => set("phone", v)} placeholder="+91 XXXXX XXXXX" type="tel" />
              </div>

              <Field
                label={isCourier ? t.checkout.delivery_address : t.checkout.notes_optional}
                value={isCourier ? form.address : form.notes}
                onChange={v => isCourier ? set("address", v) : set("notes", v)}
                placeholder={isCourier ? t.checkout.delivery_address : t.checkout.pickup_notes_ph}
                multiline
              />

              {isCourier && (
              <>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t.checkout.city} value={form.city} onChange={v => set("city", v)} placeholder={t.checkout.city} />
                <SelectField
                  label={t.checkout.state}
                  value={form.state}
                  onChange={v => set("state", v)}
                  placeholder="Select your delivery State"
                  options={Object.entries(settings.stateShippingRates).map(([state, rate]) => ({
                    value: state,
                    label: `${state} (Base: ₹${rate}/kg)`,
                  }))}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Field label={t.checkout.pincode} value={form.pincode} onChange={v => set("pincode", v)} placeholder="500001" />
                <SelectField
                  label={t.checkout.country}
                  value={form.country}
                  onChange={v => set("country", v)}
                  options={[{ value: "India", label: "India" }, { value: "Other", label: "Other" }]}
                />
              </div>

              {isNonIndia ? (
                <div className="bg-[#7A3B25]/10 border border-[#7A3B25]/30 rounded-sm p-4 text-center space-y-2" data-testid="non-india-block">
                  <div className="text-sm font-bold text-[#7A3B25]">For international orders, please contact us.</div>
                  <p className="text-xs text-[#2E1A12]/70">We'll help you arrange delivery outside India over WhatsApp.</p>
                  <p className="text-[11px] text-[#2E1A12]/50">Redirecting you to WhatsApp…</p>
                  <a
                    href={WA_INTL_MESSAGE}
                    onClick={() => track("checkout_non_india_whatsapp_click", { source: "checkout_country_block" })}
                    className="inline-flex items-center gap-2 bg-[#556B2F] hover:bg-[#6a8639] text-white px-4 py-2 rounded-full text-sm font-bold transition-colors"
                  >
                    Contact us on WhatsApp
                  </a>
                </div>
              ) : (
                <>
                  {user && selectedAddressId === null && (
                    <label className="flex items-center gap-2 text-sm text-[#2E1A12]/80 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={saveNewAddress}
                        onChange={(e) => setSaveNewAddress(e.target.checked)}
                        className="w-4 h-4 rounded-sm border-[#5A2D1D]/40 text-[#5A2D1D] focus:ring-[#5A2D1D]"
                      />
                      Save this address for next time
                    </label>
                  )}

                  <Field
                    label={t.checkout.notes_optional}
                    value={form.notes}
                    onChange={v => set("notes", v)}
                    placeholder={t.checkout.notes_optional}
                  />
                </>
              )}
              </>
              )}
            </div>

            {settings.enableQrCheckout ? (
              <>
                <button
                  onClick={handlePlaceOrder}
                  disabled={items.length === 0 || isNonIndia}
                  className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-[#5A2D1D] hover:bg-[#7A3B25] disabled:bg-[#5A2D1D]/40 text-[#F5E6CC] py-4 rounded-full font-bold text-lg transition-colors shadow-md"
                >
                  {`${t.checkout.proceed_to_payment} · ${formatPrice(grandTotal)}`}
                </button>
                <p className="text-xs text-[#5A2D1D] font-bold text-center mt-3">{t.checkout.next_step_upi}</p>
              </>
            ) : (
              <button
                onClick={handleDirectPhonePe}
                disabled={items.length === 0 || phonePeLoading || isNonIndia}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#5520A8] hover:bg-[#6B30C7] disabled:bg-[#5520A8]/40 text-white py-4 rounded-full font-bold text-lg transition-colors shadow-md"
              >
                {phonePeLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                {phonePeLoading ? "Redirecting to PhonePe…" : `Make Payment · ${formatPrice(grandTotal)}`}
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

const UtrPopup = ({ onSubmit, onSkip }) => {
  const [utr, setUtr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!utr.trim()) { toast.error("Please enter the UTR number, or skip and add it later."); return; }
    setSaving(true);
    try {
      await onSubmit(utr.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#2E1A12]/60 backdrop-blur-sm">
      <div className="w-full max-w-sm bg-[#F5E6CC] border border-[#5A2D1D]/25 rounded-sm shadow-2xl p-6">
        <h3 className="font-display text-lg font-bold text-[#5A2D1D] mb-1">Enter UTR Number</h3>
        <p className="text-xs text-[#2E1A12]/60 mb-4">
          Adding your payment's UTR / reference number helps us verify it faster. You can skip this and add it later from your order page.
        </p>
        <input
          autoFocus
          value={utr}
          onChange={(e) => setUtr(e.target.value)}
          placeholder="e.g. 123456789012"
          className="w-full px-3 py-2.5 rounded-sm bg-white border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
        />
        <p className="mt-1.5 text-[10px] text-[#7A3B25] font-semibold">UTR is mandatory to get your order confirmed.</p>
        <div className="mt-4 flex gap-2">
          <button
            onClick={onSkip}
            className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold text-[#5A2D1D] border border-[#5A2D1D]/30 hover:bg-[#5A2D1D]/5"
          >
            Skip
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-4 py-2.5 rounded-full text-sm font-semibold disabled:opacity-60"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

const PriceLine = ({ label, amount, free, freeLabel }) => (
  <div className="flex justify-between text-sm text-[#2E1A12]">
    <span className="text-[#2E1A12]/80">{label}</span>
    <span className={`font-semibold ${amount === 0 ? "text-[#556B2F]" : "text-[#5A2D1D]"}`}>
      {amount === 0 && free ? (freeLabel || "Free") : formatPrice(amount)}
    </span>
  </div>
);

const Field = ({ label, value, onChange, placeholder, type = "text", multiline }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1.5">
      {label}
    </label>
    {multiline ? (
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full px-3 py-2.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12] placeholder:text-[#2E1A12]/40 resize-none"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12] placeholder:text-[#2E1A12]/40"
      />
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options, placeholder }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest font-bold text-[#556B2F] mb-1.5">
      {label}
    </label>
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-3 py-2.5 rounded-sm bg-[#F5E6CC] border border-[#5A2D1D]/30 focus:border-[#5A2D1D] focus:outline-none text-sm text-[#2E1A12]"
    >
      {placeholder && <option value="" disabled>{placeholder}</option>}
      {options.map(o => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </div>
);
